/*
 * oem-drawer-filter.jsx
 * Copyright (c) Harman International Industries, Incorporated, 2024 - 2025
 * All rights reserved.
 */
import {
  Box,
  Button,
  Checkbox,
  Drawer,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import WarningIcon from "@mui/icons-material/Warning";
import CloseIcon from "@mui/icons-material/Close";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Collapse from "@mui/material/Collapse";
import { useEffect, useRef, useState } from "react";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import SearchIcon from "@mui/icons-material/Search";
import "./global-admin-drawer-filter.css";
import APIManager from "../../../Services/ApiManager/ApiManager";
import PopupDialog from "../../../Components/PopupDialog";
import PropTypes from "prop-types";
import apiLeveL from "./api-level.json";

const STATUS_ARRAY = ["Approved", "Published", "Rejected"];
const PAYMENT_ARRAY = ["Premium App", "Free App"];

const resolveToAllOrSelection = (list, checkedList) => {
  if (!list || list.length === 0) return ["ALL"];
  if (list.length === checkedList.length) return ["ALL"];
  return checkedList;
};

const errorSx = { color: "red", fontSize: "13px", marginLeft: "16px", marginTop: "4px" };

const FilterError = ({ show, message }) =>
  show ? <Typography sx={errorSx}>{message}</Typography> : null;

FilterError.propTypes = {
  show: PropTypes.bool.isRequired,
  message: PropTypes.string.isRequired,
};

const DropdownArrow = ({ isOpen }) =>
  isOpen ? (
    <ArrowDropUpIcon style={{ color: "#414344 !important" }} />
  ) : (
    <ArrowDropDownIcon style={{ color: "#717273 !important" }} />
  );

DropdownArrow.propTypes = {
  isOpen: PropTypes.bool.isRequired,
};

const SelectAllButton = ({ flag, onClick }) => (
  <button type="button" className="selectAllTextCls" onClick={onClick}>
    {flag ? "Select All" : "Unselect All"}
  </button>
);

SelectAllButton.propTypes = {
  flag: PropTypes.bool.isRequired,
  onClick: PropTypes.func.isRequired,
};

const emptyWarningStyle = {
  width: "100%",
  maxWidth: "383px",
  height: "32px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  backgroundColor: "#f8f9fa",
  border: "1px solid #e9ecef",
  borderRadius: "4px",
  margin: "0 auto",
  padding: "0 8px",
  boxSizing: "border-box",
};

const emptyWarningInnerStyle = {
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  gap: "6px",
};

const EmptyTypeWarning = ({ isOpen, label }) => (
  <Collapse in={isOpen} timeout="auto" unmountOnExit>
    <div className="listDivCls" style={emptyWarningStyle}>
      <div style={emptyWarningInnerStyle}>
        <WarningIcon style={{ fontSize: "12px", color: "#6c757d" }} />
        <span style={{ fontSize: "12px", color: "#6c757d", fontWeight: "500", whiteSpace: "nowrap" }}>
          {label}
        </span>
      </div>
    </div>
  </Collapse>
);

EmptyTypeWarning.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  label: PropTypes.string.isRequired,
};

export const GlobalAdminDrawerFilter = ({
  openFilter,
  toggleDrawer,
  categoryObjectList,
  setCategoryObjectList,
  modelObjectList,
  handleFilterApiCalls,
  channelListchecked,
  setChannelListchecked,
  setChannelSelections,
  categorySelection,
  setCategorySelection,
  setModelSelections,
  paymentSelections,
  setPaymentSelections,
  countrySelections,
  setCountrySelections,
  statusSelections,
  setStatusSelections,
  apiLevelSelections,
  setApiLevelSelections,
  setEcuTypeSelections,

  setCpuTypeSelections,
}) => {
  const [countryObjectList, setCountryObjectList] = useState([]);
  const [ECUTypesObjectList, setECUTypesObjectList] = useState([]);
  const [CPUTypesObjectList, setCPUTypesObjectList] = useState([]);
  const [apiObjectList, setApiObjectList] = useState([]);
  const [countryObjResponse, setCountryObjResponse] = useState([]);

  const [statusOpen, setStatusOpen] = useState(false);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [ecuOpen, setECUOpen] = useState(false);
  const [cpuOpen, setCpuOpen] = useState(false);
  const [oemOpen, setOemOpen] = useState(false);
  const [countryOpen, setCountryOpen] = useState(false);
  const [ApiLevelOpen, setApiLevelOpen] = useState(false);
  const [paymentOpen, setPaymentOpen] = useState(false);

  const [selectAllCategoryFlag, setSelectAllCategoryFlag] = useState(false);
  const [selectAllCountryFlag, setSelectAllCountryFlag] = useState(false);
  const [selectAllOEMFlag, setSelectAllOEMFlag] = useState(false);
  const [selectAllApiFlag, setSelectAllApiFlag] = useState(false);

  const [statusListchecked, setStatusListchecked] = useState([]);
  const [cpuListchecked, setCPUListchecked] = useState([]);
  const [categoryListChecked, setCategoryListChecked] = useState([]);
  const [modelListChecked, setModelListChecked] = useState([]);
  const [ecuListchecked, setECUListchecked] = useState([]);
  const [oemListchecked, setOemListchecked] = useState([]);
  const [countryListchecked, setCountryListchecked] = useState([]);
  const [apiLevelListchecked, setApiLevelListchecked] = useState([]);
  const [paymentListchecked, setPaymentListchecked] = useState([]);

  const [searchCategory, setSearchCategory] = useState("");
  const [searchECU, setSearchECU] = useState("");
  const [searchCPU, setSearchCPU] = useState("");
  const [searchOEM, setSearchOEM] = useState("");
  const [searchCountry, setSearchCountry] = useState("");
  const [searchApiLevel, setSearchApiLevel] = useState("");

  const [componentName, setComponentName] = useState("");
  const [openErrorPopup, setOpenErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [headerMessage, setHeaderMessage] = useState("");
  const [OEMObjectList, setOEMObjectList] = useState([]);

  const [appType, setAppType] = useState("3rd Party Apps");

  const [showStatusError, setShowStatusError] = useState(false);
  const [showCategoryError, setShowCategoryError] = useState(false);
  const [showCountryError, setShowCountryError] = useState(false);
  const [showApilevelError, setShowApilevelError] = useState(false);
  const [showPaymentError, setShowPaymentError] = useState(false);
  const [tabChange, setTabChange] = useState(false);
  const categoriesList = useRef();
  const modelList = useRef();
  const ecuList = useRef();
  const countryList = useRef();
  const apiLevelList = useRef();
  const hasRestoredRef = useRef(false);

  const clearSavedFilterState = () => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId && portfolioId !== "null") {
      const filterStateKey = `oemFilterState_${portfolioId}`;
      try {
        sessionStorage.removeItem(filterStateKey);
      } catch (error) {
        console.error("Failed to clear saved filter state:", error);
      }
    }
  };

   const makeFalseDrawFilterValues = (whichNeedsToOpen, whichNeedsToOpenBooleanValue) => {
    setStatusOpen(whichNeedsToOpen === "status"? whichNeedsToOpenBooleanValue : false)
    setCategoryOpen(whichNeedsToOpen === "category" ? whichNeedsToOpenBooleanValue : false);
    setECUOpen(whichNeedsToOpen === "ecu" ? whichNeedsToOpenBooleanValue : false);
    setCpuOpen(whichNeedsToOpen === "cpu" ? whichNeedsToOpenBooleanValue : false);
    setOemOpen(whichNeedsToOpen === "oem" ? whichNeedsToOpenBooleanValue : false);
    setCountryOpen(whichNeedsToOpen === "country" ? whichNeedsToOpenBooleanValue : false);
    setApiLevelOpen(whichNeedsToOpen === "api" ? whichNeedsToOpenBooleanValue : false);
    setPaymentOpen(whichNeedsToOpen === "payment" ? whichNeedsToOpenBooleanValue : false);

  };

  const handleStatusClick = () => {
    const willOpen = !statusOpen;
     makeFalseDrawFilterValues("status", willOpen)
   
  };

 
  const handleCategoryClick = () => {
    const willOpen = !categoryOpen;
      makeFalseDrawFilterValues("category",willOpen);
  
 
  };

  const handleECUClick = () => {
    const willOpen = !ecuOpen;
  
     makeFalseDrawFilterValues("ecu", willOpen);
  
 
  };

  const handleCPUClick = () => {
    const willOpen = !cpuOpen;
    
     makeFalseDrawFilterValues("cpu", willOpen);
    
  
  };

  const handleOemClick = () => {
    const willOpen = !oemOpen;
 
     makeFalseDrawFilterValues("oem", willOpen)
    
  };

  const handleCountryClick = () => {
    const willOpen = !countryOpen;
   
      makeFalseDrawFilterValues("country", willOpen)
    
  };

  const handleApiLevelClick = () => {
    const willOpen = !ApiLevelOpen;
  
    makeFalseDrawFilterValues("api", willOpen)
   
  };

  const handlePaymentClick = () => {
    const willOpen = !paymentOpen;
   
      makeFalseDrawFilterValues("payment", willOpen)

  };

  const categoryMatchesSearch = (item) =>
    item.toLowerCase().includes(searchCategory.toLowerCase()) && item !== "";

  const filteredCategory = Object.keys(categoryObjectList).filter(categoryMatchesSearch);

  const filteredECUTypes = ECUTypesObjectList?.length > 0
    ? ECUTypesObjectList.filter((item) => item.toLowerCase().includes(searchECU.toLowerCase()))
    : [];

  const filteredCPUTypes = CPUTypesObjectList?.length > 0
    ? CPUTypesObjectList.filter((item) => item.toLowerCase().includes(searchCPU.toLowerCase()))
    : [];

  const filteredOEM = OEMObjectList.filter((item) =>
    item.toLowerCase().includes(searchOEM.toLowerCase())
  );

  const filteredCountry = countryObjectList.filter((item) =>
    item.toLowerCase().includes(searchCountry.toLowerCase())
  );

  const matchesApiLevelSearch = (item) => {
    const itemVal = Number.parseInt(item);
    const searchVal = Number.parseInt(searchApiLevel);
    if (!searchApiLevel || Number.isNaN(searchVal)) return true;
    return itemVal >= searchVal;
  };

  const filteredApiLevel = apiObjectList?.filter(matchesApiLevelSearch) || [];

  const createToggleHandler = (list, setList, errorSetter = null, refToUpdate = null) => (value) => () => {
    const currentIndex = list.indexOf(value);
    const newChecked = [...list];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setList(newChecked);
    
    if (errorSetter && newChecked.length > 0) {
      errorSetter(false);
    }
    
    if (refToUpdate) {
      refToUpdate.current = newChecked.length;
    }
  };

  const handleStatusToggle = createToggleHandler(statusListchecked, setStatusListchecked, setShowStatusError);
  const handleCategoryToggle = createToggleHandler(categoryListChecked, setCategoryListChecked, setShowCategoryError, categoriesList);
  const handleECUToggle = createToggleHandler(ecuListchecked, setECUListchecked, null, ecuList);
  const handleCPUToggle = createToggleHandler(cpuListchecked, setCPUListchecked);
  const handleOemToggle = createToggleHandler(oemListchecked, setOemListchecked);
  const handleCountryToggle = createToggleHandler(countryListchecked, setCountryListchecked, setShowCountryError, countryList);
  const handleAPILevelToggle = createToggleHandler(apiLevelListchecked, setApiLevelListchecked, setShowApilevelError, apiLevelList);
  const handlePaymentToggle = createToggleHandler(paymentListchecked, setPaymentListchecked, setShowPaymentError);

  const saveFilterState = (data) => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId && portfolioId !== "null") {
      const filterStateKey = `oemFilterState_${portfolioId}`;
      const filterState = {
        appType,
        categoryListChecked,
        ecuListchecked,
        cpuListchecked,
        oemListchecked,
        countryListchecked,
        apiLevelListchecked,
        statusListchecked,
        paymentListchecked,
        lastAppliedFilterData: data,
        timestamp: Date.now(),
      };
      try {
        sessionStorage.setItem(filterStateKey, JSON.stringify(filterState));
      } catch (error) {
        console.error("Failed to save filter state to sessionStorage:", error);
      }
    }
  };

  const handleApplyFilters = () => {
    const isStatusValid = statusListchecked.length > 0;
    setShowStatusError(!isStatusValid);

    const isCategoryValid = categoryListChecked.length > 0;
    setShowCategoryError(!isCategoryValid);

    const isCountryValid = countryListchecked.length > 0;
    setShowCountryError(!isCountryValid);

    const isApilevelValid = apiLevelListchecked.length > 0;
    setShowApilevelError(!isApilevelValid);

    const isPaymentValid = paymentListchecked.length > 0;
    setShowPaymentError(!isPaymentValid);

    const ecuTypesValue = resolveToAllOrSelection(ECUTypesObjectList, ecuListchecked);
    const cpuTypesValue = resolveToAllOrSelection(CPUTypesObjectList, cpuListchecked);
    const oemValue = resolveToAllOrSelection(OEMObjectList, oemListchecked);

    const countryIso3Codes = countryObjResponse
      .filter((country) => countryListchecked.includes(country.name))
      .map((country) => country.iso3Code);

    const data = {
      categories:
        Object.keys(categoryObjectList).length ===
        Object.keys(categoryListChecked).length
          ? ["ALL"]
          : categoryListChecked,
      ecuTypes: ecuTypesValue,
      cpuTypes: cpuTypesValue,
      oem: oemValue,
      country:
        countryObjectList.length === countryListchecked.length
          ? ["ALL"]
          : countryIso3Codes,
      apiLevel:
        apiLevelListchecked.length === apiObjectList.length
          ? ["ALL"]
          : apiLevelListchecked,
      payment:
        PAYMENT_ARRAY.length === paymentListchecked.length
          ? ["ALL"]
          : paymentListchecked,
      appStatus: statusListchecked,
      searchText: [],
    };

    setCategorySelection((prev) => ({ ...prev, [appType]: categoryListChecked }));
    setModelSelections((prev) => ({ ...prev, [appType]: modelListChecked }));
    setCountrySelections((prev) => ({ ...prev, [appType]: countryListchecked }));
    setPaymentSelections((prev) => ({ ...prev, [appType]: paymentListchecked }));
    setStatusSelections((prev) => ({ ...prev, [appType]: statusListchecked }));
    setApiLevelSelections((prev) => ({ ...prev, [appType]: apiLevelListchecked }));
    setEcuTypeSelections((prev) => ({ ...prev, [appType]: ecuListchecked }));
    setCpuTypeSelections((prev) => ({ ...prev, [appType]: cpuListchecked }));

    saveFilterState(data);
    handleFilterApiCalls(data);
  };

  const handleResetChange = () => {
    setAppType("3rd Party Apps");
    setStatusListchecked(["Approved", "Published"]);
    setPaymentListchecked(PAYMENT_ARRAY);

    setApiLevelListchecked(apiLeveL.map((item) => item.name));
    setApiObjectList(apiLeveL.map((item) => item.name));

    setCategoryListChecked(
      Object.keys(categoryObjectList).filter((item) => item !== "")
    );

    setECUListchecked(ECUTypesObjectList || []);
    setCPUListchecked(CPUTypesObjectList || []);

    setCountryListchecked(countryObjectList);
    setOemListchecked(OEMObjectList);

    setSelectAllCategoryFlag(false);
    setSelectAllCountryFlag(false);
    setSelectAllApiFlag(false);
    setSelectAllOEMFlag(false);

    setSearchCategory("");
    setSearchECU("");
    setSearchCPU("");
    setSearchCountry("");
    setSearchApiLevel("");
    setSearchOEM("");

    setCategorySelection({});
    setModelSelections({});
    setCountrySelections({});
    setPaymentSelections({});
    setStatusSelections({});
    setApiLevelSelections({});
    setEcuTypeSelections({});
    setCpuTypeSelections({});
    setChannelSelections({});

    clearSavedFilterState();

    const defaultData = {
      categories: ["ALL"],
      ecuTypes: ["ALL"],
      cpuTypes: ["ALL"],
      oem: ["ALL"],
      country: ["ALL"],
      apiLevel: ["ALL"],
      payment: ["ALL"],
      appStatus: ["Approved", "Published"],
      searchText: [],
    };
    handleFilterApiCalls(defaultData);
  };

  const createUnselectHandler = (flag, setFlag, setList, getSourceData, refToUpdate = null) => () => {
    setFlag(!flag);
    if (flag) {
      const data = getSourceData();
      setList(data);
      if (refToUpdate) {
        refToUpdate.current = data.length;
      }
    } else {
      setList([]);
      if (refToUpdate) {
        refToUpdate.current = 0;
      }
    }
  };

  const handleUnselectCategory = createUnselectHandler(
    selectAllCategoryFlag,
    setSelectAllCategoryFlag,
    setCategoryListChecked,
    () => filteredCategory.filter(item => item !== ""),
    categoriesList
  );

  const handleUnselectOEM = createUnselectHandler(
    selectAllOEMFlag,
    setSelectAllOEMFlag,
    setOemListchecked,
    () => OEMObjectList
  );

  const handleUnselectCountry = createUnselectHandler(
    selectAllCountryFlag,
    setSelectAllCountryFlag,
    setCountryListchecked,
    () => countryObjectList,
    countryList
  );

  const handleUnselectApiLevel = createUnselectHandler(
    selectAllApiFlag,
    setSelectAllApiFlag,
    setApiLevelListchecked,
    () => apiObjectList,
    apiLevelList
  );

  const createSearchHandler = (setter, preventDefault = false) => (event) => {
    if (preventDefault) {
      event.preventDefault();
    }
    setter(event.target.value);
  };

  const handleCategorySearch = createSearchHandler(setSearchCategory);
  const handleOemSearch = createSearchHandler(setSearchOEM);
  const handleCountrySearch = createSearchHandler(setSearchCountry);
  const handleApiLevelSearch = createSearchHandler(setSearchApiLevel, true);

  const createSimpleToggleAllHandler = (checkedList, sourceList, setList) => () => {
    setList(checkedList.length === sourceList.length ? [] : sourceList);
  };

  const createFilteredToggleAllHandler = (checkedList, filteredList, setList, refToUpdate = null) => () => {
    const allChecked = filteredList.length > 0 && filteredList.every(item => checkedList.includes(item));
    const newChecked = allChecked
      ? checkedList.filter(item => !filteredList.includes(item))
      : [...new Set([...checkedList, ...filteredList])];
    
    setList(newChecked);
    if (refToUpdate) {
      refToUpdate.current = newChecked.length;
    }
  };

  const handleAllStatusToggle = createSimpleToggleAllHandler(statusListchecked, STATUS_ARRAY, setStatusListchecked);
  const handleAllCategoryToggle = createFilteredToggleAllHandler(categoryListChecked, filteredCategory, setCategoryListChecked, categoriesList);
  const handleAllEcuToggle = createFilteredToggleAllHandler(ecuListchecked, filteredECUTypes, setECUListchecked, ecuList);
  const handleAllCpuToggle = createFilteredToggleAllHandler(cpuListchecked, filteredCPUTypes, setCPUListchecked);
  const handleAllOemToggle = createFilteredToggleAllHandler(oemListchecked, filteredOEM, setOemListchecked);
  const handleAllCountryToggle = createFilteredToggleAllHandler(countryListchecked, filteredCountry, setCountryListchecked, countryList);
  const handleAllApilevelToggle = createFilteredToggleAllHandler(apiLevelListchecked, filteredApiLevel, setApiLevelListchecked, apiLevelList);
  const handleAllPaymentToggle = createSimpleToggleAllHandler(paymentListchecked, PAYMENT_ARRAY, setPaymentListchecked);

  useEffect(() => {
    if (!tabChange) return;
    setCategoryListChecked(
      Object.keys(categoryObjectList).filter((item) => item !== "")
    );
    setPaymentListchecked(PAYMENT_ARRAY);
    setStatusListchecked(["Approved", "Published"]);
    setApiLevelListchecked(apiLeveL.map((item) => item.name));
    setECUListchecked(ECUTypesObjectList || []);
    setCPUListchecked(CPUTypesObjectList || []);
    setCountryListchecked(countryObjectList);
    setOemListchecked(OEMObjectList || []);
    setChannelSelections((prev) => ({
      ...prev,
      [appType]: [],
    }));
    setCategorySelection((prev) => ({ ...prev, [appType]: [] }));
    setEcuTypeSelections((prev) => ({ ...prev, [appType]: [] }));
    setCpuTypeSelections((prev) => ({ ...prev, [appType]: [] }));
    setModelSelections((prev) => ({ ...prev, [appType]: [] }));
    setPaymentSelections((prev) => ({ ...prev, [appType]: [] }));
    setStatusSelections((prev) => ({ ...prev, [appType]: [] }));
    setApiLevelSelections((prev) => ({ ...prev, [appType]: [] }));

    setShowStatusError(false);
    setShowCategoryError(false);
    setShowCountryError(false);
    setShowApilevelError(false);
    setShowPaymentError(false);
    setTabChange(false);
  }, [tabChange, appType]);

  const resetFilterSearchInputs = () => {
    setSearchCategory("");
    setSearchECU("");
    setSearchCountry("");
    setSearchApiLevel("");
    setSearchOEM("");
    categoriesList.current = categoryListChecked.length;
    modelList.current = modelListChecked.length;
    countryList.current = countryListchecked.length;
    apiLevelList.current = apiLevelListchecked.length;
    ecuList.current = ecuListchecked.length;
  };

  const initCategoryFromSelection = () => {
    if (categorySelection == null) return;
    const categoryNames =
      categorySelection.appCountPerCategory?.map((item) => item.categoryName) || [];
    const categoryObject = {};
    categoryNames.forEach((name) => {
      categoryObject[name] = name;
    });
    if (typeof setCategoryObjectList === "function") {
      setCategoryObjectList(categoryObject);
    }
    setCategoryListChecked(categoryNames);
  };

  const handleCountriesRetryResponse = (json) => {
    const countryNames = json.map((item) => item.name);
    setCountryObjResponse(json);
    setCountryObjectList(countryNames);
    setCountryListchecked(countryNames);
  };

  useEffect(() => {
    hasRestoredRef.current = false;
    if (openFilter) {
      resetFilterSearchInputs();
    }
    const savedCategories = categoryListChecked;

    setCategoryListChecked(
      savedCategories?.length
        ? savedCategories
        : Object.keys(categoryObjectList).filter((item) => item !== "")
    );

    const savedPayments = paymentSelections[appType];
    setPaymentListchecked(savedPayments?.length ? savedPayments : PAYMENT_ARRAY);
    const savedStatus = statusSelections[appType];
    setStatusListchecked(
      savedStatus?.length ? savedStatus : ["Approved", "Published"]
    );
    const savedApiLevel = apiLevelSelections[appType];
    setApiLevelListchecked(
      savedApiLevel?.length ? savedApiLevel : apiLeveL.map((item) => item.name)
    );
    setApiObjectList(apiLeveL.map((item) => item.name));
    const portfolioId = sessionStorage.getItem("portfolioId");

    initCategoryFromSelection();

    APIManager.getAdminOEM()
      .then((json) => {
        const oemObjectList = json.map((item) => item.name);
        setOEMObjectList(oemObjectList);
        const portfolioId = sessionStorage.getItem("portfolioId");
        const filterStateKey = `oemFilterState_${portfolioId}`;
        const savedFilterState = sessionStorage.getItem(filterStateKey);

        if (savedFilterState) {
          try {
            const filterState = JSON.parse(savedFilterState);
            if (Array.isArray(filterState.oemListchecked)) {
              setOemListchecked(filterState.oemListchecked);
              return;
            }
          } catch (error) {
            console.error("Failed to parse saved OEM filter state:", error);
          }
        }

        setOemListchecked(oemObjectList);
      })
      .catch((error) => {
        console.error("OEM API retry failed:", error);
      });

    if (portfolioId && portfolioId !== "null") {
      APIManager.getGlobalAdminCountries()
        .then((json) => {
          const countryNames = json.map((item) => item.name);
          setCountryObjResponse(json);
          setCountryObjectList(countryNames);
          const savedCountry = countrySelections[appType];
          setCountryListchecked(
            savedCountry?.length ? savedCountry : countryNames
          );

          if (tabChange) {
            setCountryListchecked(countryNames);
            setCountrySelections((prev) => ({ ...prev, [appType]: [] }));
            setTabChange(false);
          }
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
        });

      APIManager.getAdminCPUTypes()
        .then((json) => {
          setCPUTypesObjectList(json);
          const filterStateKey = `oemFilterState_${portfolioId}`;
          const savedFilterState = sessionStorage.getItem(filterStateKey);

          if (savedFilterState) {
            try {
              const filterState = JSON.parse(savedFilterState);
              if (Array.isArray(filterState.cpuListchecked)) {
                setCPUListchecked(filterState.cpuListchecked);
                return;
              }
            } catch (error) {
              console.error("Failed to parse saved CPU filter state:", error);
            }
          }

          setCPUListchecked(json);
        })
        .catch((error) => {
          console.error("ECU Types API retry failed:", error);
        });

      // Call ECU Types API
      APIManager.getGlobalAdminEcuTypes()
        .then((json) => {
          const ecuTypes =
            json?.length > 0
              ? [
                  ...new Set(
                    json
                      .flatMap((item) => item.ecuTypes || [])
                      .filter(Boolean)
                  ),
                ]
              : [];
          setECUTypesObjectList(ecuTypes);
          const filterStateKey = `oemFilterState_${portfolioId}`;
          const savedFilterState = sessionStorage.getItem(filterStateKey);

          if (savedFilterState) {
            try {
              const filterState = JSON.parse(savedFilterState);
              if (Array.isArray(filterState.ecuListchecked)) {
                setECUListchecked(filterState.ecuListchecked);
                setEcuTypeSelections(json);
                if (tabChange) {
                  setECUTypesObjectList(json);
                  setTabChange(false);
                }
                return;
              }
            } catch (error) {
              console.error("Failed to parse saved ECU filter state:", error);
            }
          }

          setECUListchecked(ecuTypes);
          setEcuTypeSelections(json);
          if (tabChange) {
            setECUTypesObjectList(json);
            setTabChange(false);
          }
        })
        .catch((error) => {
          setComponentName("appError");
          setErrorMessage(error.message);
          setHeaderMessage(
            error.message === "jwt expired" ? "401 Token Expired" : "Error"
          );
          setOpenErrorPopup(true);
        });
    } else {
      console.warn(
        "Countries and ECU Types API calls skipped: Portfolio ID is not available"
      );
      setCountryObjResponse([]);
      setCountryObjectList([]);
      setCountryListchecked([]);
      setECUTypesObjectList([]);
      setECUListchecked([]);

      if (openFilter) {
        let retryAttempts = 0;
        const maxRetries = 3;

        const checkPortfolio = () => {
          const currentPortfolioId = sessionStorage.getItem("portfolioId");
          if (currentPortfolioId && currentPortfolioId !== "null") {
            APIManager.getAdminCountries()
              .then(handleCountriesRetryResponse)
              .catch((error) => {
                console.error("Countries API retry failed:", error);
              });

            APIManager.getAdminCPUTypes()
              .then((json) => {
                setCPUTypesObjectList(json);
                const filterStateKey = `oemFilterState_${currentPortfolioId}`;
                const savedFilterState = sessionStorage.getItem(filterStateKey);

                if (savedFilterState) {
                  try {
                    const filterState = JSON.parse(savedFilterState);
                    if (Array.isArray(filterState.cpuListchecked)) {
                      setCPUListchecked(filterState.cpuListchecked);
                      return;
                    }
                  } catch (error) {
                    console.error(
                      "Failed to parse saved CPU filter state in retry:",
                      error
                    );
                  }
                }

                setCPUListchecked(json);
              })
              .catch((error) => {
                console.error("ECU Types API retry failed:", error);
              });
          } else if (retryAttempts < maxRetries) {
            retryAttempts++;
            setTimeout(checkPortfolio, 1000);
          } else {
            console.warn(
              "Max retry attempts reached, portfolio ID still not available"
            );
          }
        };

        setTimeout(checkPortfolio, 500);
      }
    }
  }, [openFilter]);

  const restoreFilterSelections = (filterState, currentAppType) => {
    if (filterState.categoryListChecked)
      setCategoryListChecked(filterState.categoryListChecked);
    if (filterState.modelListChecked)
      setECUListchecked(filterState.ecuListchecked);
    if (filterState.cpuListchecked)
      setCPUListchecked(filterState.cpuListchecked);
    if (filterState.oemListchecked)
      setOemListchecked(filterState.oemListchecked);
    if (filterState.countryListchecked)
      setCountryListchecked(filterState.countryListchecked);
    if (filterState.apiLevelListchecked)
      setApiLevelListchecked(filterState.apiLevelListchecked);
    if (filterState.statusListchecked)
      setStatusListchecked(filterState.statusListchecked);
    if (filterState.paymentListchecked)
      setPaymentListchecked(filterState.paymentListchecked);
    setChannelSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.channelListchecked || [],
    }));
    setCategorySelection((prev) => ({
      ...prev,
      [currentAppType]: filterState.categoryListChecked || [],
    }));
    setModelSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.modelListChecked || [],
    }));
    setEcuTypeSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.ecuListchecked || [],
    }));
    setCpuTypeSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.cpuListchecked || [],
    }));
    setCountrySelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.countryListchecked || [],
    }));
    setApiLevelSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.apiLevelListchecked || [],
    }));
    setStatusSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.statusListchecked || [],
    }));
    setPaymentSelections((prev) => ({
      ...prev,
      [currentAppType]: filterState.paymentListchecked || [],
    }));
  };

  const applyFilterStateFromStorage = (filterStateKey, currentAppType) => {
    const savedFilterState = sessionStorage.getItem(filterStateKey);
    if (!savedFilterState) return;

    try {
      const filterState = JSON.parse(savedFilterState);

      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - filterState.timestamp > maxAge) {
        sessionStorage.removeItem(filterStateKey);
        return;
      }

      if (filterState.appType && filterState.appType !== currentAppType) {
        setAppType(filterState.appType);
        return;
      }
      if (filterState.appType === currentAppType) {
        setTimeout(() => restoreFilterSelections(filterState, currentAppType), 100);
      }
    } catch (error) {
      console.error("Failed to parse saved filter state:", error);
      sessionStorage.removeItem(filterStateKey);
    }
  };

  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (!portfolioId || portfolioId === "null" || !openFilter) return;

    const filterStateKey = `oemFilterState_${portfolioId}`;
    applyFilterStateFromStorage(filterStateKey, appType);
  }, [openFilter, appType]);

  useEffect(() => {
    setShowStatusError(statusListchecked.length === 0);
    setShowCategoryError(categoryListChecked.length === 0);
    setShowApilevelError(apiLevelListchecked.length === 0);
    setShowPaymentError(paymentListchecked.length === 0);

    categoriesList.current = categoryListChecked.length;
    modelList.current = modelListChecked.length;
    countryList.current = countryListchecked.length;
    apiLevelList.current = apiLevelListchecked.length;
    ecuList.current = ecuListchecked.length;
  }, [
    channelListchecked,
    statusListchecked,
    categoryListChecked,
    modelListChecked,
    ecuListchecked,
    countryListchecked,
    apiLevelListchecked,
    paymentListchecked,
  ]);

  useEffect(() => {
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (portfolioId && portfolioId !== "null") {
      const filterStateKey = `oemFilterState_${portfolioId}`;
      const savedFilterState = sessionStorage.getItem(filterStateKey);

      if (savedFilterState) {
        try {
          const filterState = JSON.parse(savedFilterState);

          const maxAge = 24 * 60 * 60 * 1000; 
          if (Date.now() - filterState.timestamp > maxAge) {
            sessionStorage.removeItem(filterStateKey);
            return;
          }

          if (filterState.appType && filterState.appType !== appType) {
            setAppType(filterState.appType);
          }
        } catch (error) {
          console.error(
            "Failed to parse saved filter state for appType:",
            error
          );
          sessionStorage.removeItem(filterStateKey);
        }
      }
    }
  }, [categoryObjectList, modelObjectList]);

  const restoreFilterStateOnMount = (filterStateKey, currentAppType) => {
    const savedFilterState = sessionStorage.getItem(filterStateKey);
    if (!savedFilterState) return;

    try {
      const filterState = JSON.parse(savedFilterState);

      const maxAge = 24 * 60 * 60 * 1000;
      if (Date.now() - filterState.timestamp > maxAge) {
        sessionStorage.removeItem(filterStateKey);
        return;
      }

      if (filterState.appType !== currentAppType) return;

      if (filterState.channelListchecked)
        setChannelListchecked(filterState.channelListchecked);
      if (filterState.categoryListChecked)
        setCategoryListChecked(filterState.categoryListChecked);
      if (filterState.modelListChecked)
        setModelListChecked(filterState.modelListChecked);
      if (filterState.ecuListchecked)
        setECUListchecked(filterState.ecuListchecked);
      if (filterState.countryListchecked)
        setCountryListchecked(filterState.countryListchecked);
      if (filterState.apiLevelListchecked)
        setApiLevelListchecked(filterState.apiLevelListchecked);
      if (filterState.statusListchecked)
        setStatusListchecked(filterState.statusListchecked);
      if (filterState.paymentListchecked)
        setPaymentListchecked(filterState.paymentListchecked);

      setChannelSelections((prev) => ({ ...prev, [currentAppType]: filterState.channelListchecked || [] }));
      setCategorySelection((prev) => ({ ...prev, [currentAppType]: filterState.categoryListChecked || [] }));
      setModelSelections((prev) => ({ ...prev, [currentAppType]: filterState.modelListChecked || [] }));
      setEcuTypeSelections((prev) => ({ ...prev, [currentAppType]: filterState.ecuListchecked || [] }));
      setCountrySelections((prev) => ({ ...prev, [currentAppType]: filterState.countryListchecked || [] }));
      setApiLevelSelections((prev) => ({ ...prev, [currentAppType]: filterState.apiLevelListchecked || [] }));
      setStatusSelections((prev) => ({ ...prev, [currentAppType]: filterState.statusListchecked || [] }));
      setPaymentSelections((prev) => ({ ...prev, [currentAppType]: filterState.paymentListchecked || [] }));
    } catch (error) {
      console.error("Failed to parse saved filter state on mount:", error);
      sessionStorage.removeItem(filterStateKey);
    }
  };

  useEffect(() => {
    if (hasRestoredRef.current) return;
    const portfolioId = sessionStorage.getItem("portfolioId");
    if (!portfolioId || portfolioId === "null" || !categoryObjectList || !modelObjectList) return;

    const filterStateKey = `oemFilterState_${portfolioId}`;
    restoreFilterStateOnMount(filterStateKey, appType);
    hasRestoredRef.current = true;
  }, [categoryObjectList, modelObjectList, appType]);

  return (
    <>
      <Drawer
        open={openFilter}
        onClose={toggleDrawer(false)}
        anchor="right"
        sx={{
          "& .MuiDrawer-paper": {
            width: {
              xs: "100vw",
              sm: "426px",
              md: "426px",
            },
            maxWidth: "100vw",
            overflow: "hidden",
          },
        }}
        slotProps={{
          paper: { className: "global-admin-filter-drawer" },
        }}
      >
        <div
          className="filterContainer"
          style={{
            display: "flex",
            flexDirection: "column",
            height: "100vh",
            width: "100%",
            maxWidth: "100%",
            overflow: "hidden",
            boxSizing: "border-box",
          }}
        >
          <div className="filterHeaderCls">
            <span>Filter</span>
            <span>
              <CloseIcon
                className="closeIconCls"
                onClick={toggleDrawer(false)}
              />
            </span>
          </div>
          <div
            className="filterContentCls"
            style={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              paddingBottom: "16px",
              width: "100%",
              maxWidth: "100%",
              boxSizing: "border-box",
            }}
          >
            <div>
              <Box
                sx={{
                  backgroundColor: "#f5f5f5",
                  padding: "12px 16px",
                  borderRadius: "4px",
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "14px",
                  fontWeight: 500,
                  color: "#121415",
                  margin: "16px 0",
                }}
              >
                Showing all the Apps from 3rd Party
              </Box>
            </div>
            <FilterError show={showStatusError} message="Status cannot be blank.please select a value!" />
            <FilterError show={showCategoryError} message="Category cannot be blank.please select a value!" />
            <FilterError show={showCountryError} message="Country cannot be blank.please select a value!" />
            <FilterError show={showApilevelError} message="API Level cannot be blank.please select a value!" />
            <FilterError show={showPaymentError} message="Payment cannot be blank.please select a value!" />
           
            {appType === "3rd Party Apps" && <div>3rd Third Party</div> && (
              <div>
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="status"
                >
                  <ListItemButton
                    onClick={handleStatusClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={statusOpen} />

                    <ListItemText
                      primary="Status"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {statusListchecked.length}/{STATUS_ARRAY.length}
                    </span>
                  </ListItemButton>

                  <Collapse in={statusOpen} timeout="auto" unmountOnExit>
                    <div className="listDivCls">
                      {/* ALL checkbox */}
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllStatusToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                statusListchecked.length === STATUS_ARRAY.length
                              }
                              indeterminate={
                                statusListchecked.length > 0 &&
                                statusListchecked.length < STATUS_ARRAY.length
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                statusListchecked.length === STATUS_ARRAY.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {STATUS_ARRAY
                        .filter((status) => status !== "")
                        .map((status) => (
                          <List
                            component="div"
                            key={status}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: statusListchecked.includes(
                                  status
                                )
                                  ? "#E5F3FF!important"
                                  : "",
                                color: statusListchecked.includes(status)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",

                                cursor: "default",
                              }}
                              onClick={handleStatusToggle(status)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={statusListchecked.includes(status)}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{ "aria-labelledby": status }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: statusListchecked.includes(status)
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={status}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="category"
                >
                  <ListItemButton
                    onClick={handleCategoryClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={categoryOpen} />

                    <ListItemText
                      primary="Categories"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {categoryListChecked.length}/
                      {
                        Object.keys(categoryObjectList).filter(
                          (item) => item !== ""
                        ).length
                      }
                    </span>
                  </ListItemButton>

                  <Collapse in={categoryOpen} timeout="auto" unmountOnExit>
                    <div className="versionListDivCls">
                      <Box
                        className="searchCreateBtnCls"
                        component="form"
                        noValidate
                        autoComplete="off"
                      >
                        <SelectAllButton flag={selectAllCategoryFlag} onClick={handleUnselectCategory} />

                        <TextField
                          type="search"
                          placeholder="Search Categories with first 3 characters"
                          size="small"
                          style={{
                            backgroundColor: "white",
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                          onChange={handleCategorySearch}
                        />
                      </Box>
                    </div>
                    <div className="listDivCls categoryDivCls">
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllCategoryToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                filteredCategory.length > 0 &&
                                filteredCategory.every((item) =>
                                  categoryListChecked.includes(item)
                                )
                              }
                              indeterminate={
                                filteredCategory.some((item) =>
                                  categoryListChecked.includes(item)
                                ) &&
                                !filteredCategory.every((item) =>
                                  categoryListChecked.includes(item)
                                )
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                categoryListChecked.length ===
                                filteredCategory.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {filteredCategory
                        .filter((category) => category !== "")
                        .map((category) => (
                          <List
                            component="div"
                            key={category}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: categoryListChecked.includes(
                                  category
                                )
                                  ? "#E5F3FF!important"
                                  : "",
                                color: categoryListChecked.includes(category)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",

                                cursor: "default",
                              }}
                              onClick={handleCategoryToggle(category)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={categoryListChecked.includes(
                                    category
                                  )}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{ "aria-labelledby": category }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: categoryListChecked.includes(
                                    category
                                  )
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={category}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="oems"
                >
                  <ListItemButton
                    onClick={handleOemClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={oemOpen} />

                    <ListItemText
                      primary="OEMs"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {oemListchecked.length}/{OEMObjectList?.length || 0}
                    </span>
                  </ListItemButton>

                  <Collapse in={oemOpen} timeout="auto" unmountOnExit>
                    <div className="versionListDivCls">
                      <Box
                        className="searchCreateBtnCls"
                        component="form"
                        noValidate
                        autoComplete="off"
                      >
                        <SelectAllButton flag={selectAllOEMFlag} onClick={handleUnselectOEM} />

                        <TextField
                          type="search"
                          placeholder="Search OEMs with first 3 characters"
                          size="small"
                          style={{
                            backgroundColor: "white",
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                          onChange={handleOemSearch}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Box>
                    </div>
                    <div className="listDivCls categoryDivCls">
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllOemToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                filteredOEM.length > 0 &&
                                filteredOEM.every((item) =>
                                  oemListchecked.includes(item)
                                )
                              }
                              indeterminate={
                                filteredOEM.some((item) =>
                                  oemListchecked.includes(item)
                                ) &&
                                !filteredOEM.every((item) =>
                                  oemListchecked.includes(item)
                                )
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                oemListchecked.length === filteredOEM.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {filteredOEM
                        .filter((oem) => oem !== "")
                        .map((oem) => (
                          <List
                            component="div"
                            key={oem}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: oemListchecked.includes(oem)
                                  ? "#E5F3FF!important"
                                  : "",
                                color: oemListchecked.includes(oem)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",
                                cursor: "default",
                              }}
                              onClick={handleOemToggle(oem)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={oemListchecked.includes(oem)}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{ "aria-labelledby": oem }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: oemListchecked.includes(oem)
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={oem}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
                {/* Model filter hidden for 3rd Party Apps - code removed */}
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="ecu"
                >
                  <ListItemButton
                    onClick={handleECUClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={ecuOpen} />
                    <ListItemText
                      primary="ECU Type"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {ecuListchecked.length}/{filteredECUTypes?.length || 0}
                    </span>
                  </ListItemButton>

                  {(!ECUTypesObjectList || ECUTypesObjectList.length === 0) && (
                    <EmptyTypeWarning isOpen={ecuOpen} label="No ECU Types Available" />
                  )}
                  {ECUTypesObjectList !== undefined &&
                    ECUTypesObjectList.length > 0 && (
                      <Collapse in={ecuOpen} timeout="auto" unmountOnExit>
                        <div className="versionListDivCls"></div>

                        <div className="listDivCls categoryDivCls">
                          <List component="div">
                            <ListItemButton
                              sx={{ height: "30px" }}
                              onClick={handleAllEcuToggle}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={
                                    ecuListchecked.length ===
                                    filteredECUTypes.length
                                  }
                                  indeterminate={
                                    ecuListchecked.length > 0 &&
                                    ecuListchecked.length <
                                      filteredECUTypes.length
                                  }
                                  disableRipple
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                primary="All"
                                sx={{
                                  fontSize: "14px",
                                  fontWeight:
                                    ecuListchecked.length ===
                                    filteredECUTypes.length
                                      ? "600 !important"
                                      : "500 !important",
                                }}
                              />
                            </ListItemButton>
                          </List>

                          {filteredECUTypes
                            .filter((ecu) => ecu !== "")
                            .map((ecu) => (
                              <List
                                component="div"
                                key={ecu}
                                sx={{
                                  paddingTop: "2px!important",
                                  paddingBottom: "2px!important",
                                }}
                              >
                                <ListItemButton
                                  sx={{
                                    minWidth: "0px !important",
                                    borderRadius: "4px!important",
                                    paddingTop: "0px!important",
                                    paddingBottom: "0px!important",
                                    height: "30px!important",
                                    backgroundColor: ecuListchecked.includes(
                                      ecu
                                    )
                                      ? "#E5F3FF!important"
                                      : "",
                                    color: ecuListchecked.includes(ecu)
                                      ? "#0476E2!important"
                                      : "#2A2C2D!important",

                                    cursor: "default",
                                  }}
                                  onClick={handleECUToggle(ecu)}
                                >
                                  <ListItemIcon sx={{ minWidth: "20px" }}>
                                    <Checkbox
                                      edge="start"
                                      checked={ecuListchecked.includes(ecu)}
                                      tabIndex={-1}
                                      disableRipple
                                      slotProps={{ "aria-labelledby": ecu }}
                                      sx={{ padding: 0, marginRight: 1 }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    disableTypography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: ecuListchecked.includes(ecu)
                                        ? "600!important"
                                        : "500!important",
                                    }}
                                    primary={ecu}
                                  />
                                </ListItemButton>
                              </List>
                            ))}
                        </div>
                      </Collapse>
                    )}
                </List>

                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="cpu"
                >
                  <ListItemButton
                    onClick={handleCPUClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={cpuOpen} />
                    <ListItemText
                      primary="CPU Type"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415 !important",
                        marginLeft: "-5px !important",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {
                        cpuListchecked.filter(
                          (cpu) => cpu !== "All" && cpu !== "ALL"
                        ).length
                      }
                      /
                      {
                        (
                          filteredCPUTypes?.filter(
                            (cpu) => cpu !== "ALL" && cpu !== ""
                          ) || []
                        ).length
                      }
                    </span>
                  </ListItemButton>
                  {CPUTypesObjectList?.length === 0 && (
                      <EmptyTypeWarning isOpen={cpuOpen} label="No CPU Types Available" />
                    )}
                  {CPUTypesObjectList !== undefined &&
                    CPUTypesObjectList.length > 0 && (
                      <Collapse in={cpuOpen} timeout="auto" unmountOnExit>
                        <div className="listDivCls categoryDivCls">
                          <List component="div">
                            <ListItemButton
                              sx={{ height: "30px" }}
                              onClick={handleAllCpuToggle}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={
                                    cpuListchecked.length ===
                                    filteredCPUTypes.length
                                  }
                                  indeterminate={
                                    cpuListchecked.length > 0 &&
                                    cpuListchecked.length <
                                      filteredCPUTypes.length
                                  }
                                  disableRipple
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                primary="All"
                                sx={{
                                  fontSize: "14px",
                                  fontWeight:
                                    cpuListchecked.length ===
                                    filteredCPUTypes.length
                                      ? "600 !important"
                                      : "500 !important",
                                }}
                              />
                            </ListItemButton>
                          </List>
                          {filteredCPUTypes
                            .filter((cpu) => cpu !== "" && cpu !== "ALL")
                            .map((cpu) => (
                              <List
                                component="div"
                                key={cpu}
                                sx={{
                                  paddingTop: "2px!important",
                                  paddingBottom: "2px!important",
                                }}
                              >
                                <ListItemButton
                                  sx={{
                                    minWidth: "0px !important",
                                    borderRadius: "4px!important",
                                    paddingTop: "0px!important",
                                    paddingBottom: "0px!important",
                                    height: "30px!important",
                                    backgroundColor: cpuListchecked.includes(
                                      cpu
                                    )
                                      ? "#E5F3FF!important"
                                      : "",
                                    color: cpuListchecked.includes(cpu)
                                      ? "#0476E2!important"
                                      : "#2A2C2D!important",

                                    cursor: "default",
                                  }}
                                  onClick={handleCPUToggle(cpu)}
                                >
                                  <ListItemIcon sx={{ minWidth: "20px" }}>
                                    <Checkbox
                                      edge="start"
                                      checked={cpuListchecked.includes(cpu)}
                                      tabIndex={-1}
                                      disableRipple
                                      slotProps={{ "aria-labelledby": cpu }}
                                      sx={{ padding: 0, marginRight: 1 }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    disableTypography
                                    sx={{
                                      fontSize: "14px",
                                      fontWeight: cpuListchecked.includes(cpu)
                                        ? "600!important"
                                        : "500!important",
                                    }}
                                    primary={cpu}
                                  />
                                </ListItemButton>
                              </List>
                            ))}
                        </div>
                      </Collapse>
                    )}
                </List>

                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="country"
                >
                  <ListItemButton
                    onClick={handleCountryClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={countryOpen} />

                    <ListItemText
                      primary="Country"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {countryListchecked.length}/{countryObjectList.length}
                    </span>
                  </ListItemButton>

                  <Collapse in={countryOpen} timeout="auto" unmountOnExit>
                    <div className="versionListDivCls">
                      <Box
                        className="searchCreateBtnCls"
                        component="form"
                        noValidate
                        autoComplete="off"
                      >
                        <SelectAllButton flag={selectAllCountryFlag} onClick={handleUnselectCountry} />

                        <TextField
                          type="search"
                          placeholder="Search Country with first 3 characters"
                          size="small"
                          style={{
                            backgroundColor: "white",
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                          onChange={handleCountrySearch}
                        />
                      </Box>
                    </div>
                    <div className="listDivCls categoryDivCls">
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllCountryToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                filteredCountry.length > 0 &&
                                filteredCountry.every((item) =>
                                  countryListchecked.includes(item)
                                )
                              }
                              indeterminate={
                                filteredCountry.some((item) =>
                                  countryListchecked.includes(item)
                                ) &&
                                !filteredCountry.every((item) =>
                                  countryListchecked.includes(item)
                                )
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                countryListchecked.length ===
                                filteredCountry.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {filteredCountry
                        .filter((country) => country !== "")
                        .map((country) => (
                          <List
                            component="div"
                            key={country}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: countryListchecked.includes(
                                  country
                                )
                                  ? "#E5F3FF!important"
                                  : "",
                                color: countryListchecked.includes(country)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",
                                cursor: "default",
                              }}
                              onClick={handleCountryToggle(country)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={countryListchecked.includes(country)}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{
                                    "aria-labelledby": country,
                                  }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: countryListchecked.includes(
                                    country
                                  )
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={country}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="apiLevel"
                >
                  <ListItemButton
                    onClick={handleApiLevelClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={ApiLevelOpen} />

                    <ListItemText
                      primary="API Level"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {apiLevelListchecked.length}/{apiObjectList?.length || 0}
                    </span>
                  </ListItemButton>

                  <Collapse in={ApiLevelOpen} timeout="auto" unmountOnExit>
                    {apiObjectList.length > 0 && (
                      <span
                        style={{
                          fontSize: "12px",
                          padding: "6px ",
                          marginLeft: "39px",
                        }}
                      >
                        Greater than or Equal to
                      </span>
                    )}

                    <div className="versionListDivCls">
                      <Box
                        className="searchCreateBtnCls"
                        component="form"
                        noValidate
                        autoComplete="off"
                      >
                        <SelectAllButton flag={selectAllApiFlag} onClick={handleUnselectApiLevel} />

                        <TextField
                          type="search"
                          placeholder="Enter API Level"
                          size="small"
                          value={searchApiLevel}
                          style={{
                            backgroundColor: "white",
                          }}
                          slotProps={{
                            input: {
                              startAdornment: (
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                              ),
                            },
                          }}
                          onChange={handleApiLevelSearch}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                            }
                          }}
                        />
                      </Box>
                    </div>
                    <div className="listDivCls categoryDivCls">
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllApilevelToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                filteredApiLevel.length > 0 &&
                                filteredApiLevel.every((item) =>
                                  apiLevelListchecked.includes(item)
                                )
                              }
                              indeterminate={
                                filteredApiLevel.some((item) =>
                                  apiLevelListchecked.includes(item)
                                ) &&
                                !filteredApiLevel.every((item) =>
                                  apiLevelListchecked.includes(item)
                                )
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                apiLevelListchecked.length ===
                                filteredApiLevel.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {filteredApiLevel
                        .filter((api) => api !== "")
                        .map((api) => (
                          <List
                            component="div"
                            key={api}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: apiLevelListchecked.includes(
                                  api
                                )
                                  ? "#E5F3FF!important"
                                  : "",
                                color: apiLevelListchecked.includes(api)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",

                                cursor: "default",
                              }}
                              onClick={handleAPILevelToggle(api)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={apiLevelListchecked.includes(api)}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{ "aria-labelledby": api }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: apiLevelListchecked.includes(api)
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={api}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
                <List
                  sx={{
                    width: "100%",
                    maxWidth: 383,
                    bgcolor: "background.paper",
                  }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                  id="channel"
                >
                  <ListItemButton
                    onClick={handlePaymentClick}
                    sx={{
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#121415",
                    }}
                  >
                    <DropdownArrow isOpen={paymentOpen} />

                    <ListItemText
                      primary="Payments"
                      disableTypography
                      sx={{
                        fontSize: "16px !important",
                        fontWeight: "500 !important",
                        color: "#121415",
                      }}
                    />
                    <span
                      style={{
                        fontSize: "14px",
                        color: "#121415",
                        marginLeft: "auto",
                      }}
                    >
                      {paymentListchecked.length}/{PAYMENT_ARRAY.length}
                    </span>
                  </ListItemButton>

                  <Collapse in={paymentOpen} timeout="auto" unmountOnExit>
                    <div className="listDivCls">
                      <List component="div">
                        <ListItemButton
                          sx={{ height: "30px" }}
                          onClick={handleAllPaymentToggle}
                        >
                          <ListItemIcon sx={{ minWidth: "20px" }}>
                            <Checkbox
                              edge="start"
                              checked={
                                paymentListchecked.length ===
                                PAYMENT_ARRAY.length
                              }
                              indeterminate={
                                paymentListchecked.length > 0 &&
                                paymentListchecked.length < PAYMENT_ARRAY.length
                              }
                              disableRipple
                              sx={{ padding: 0, marginRight: 1 }}
                            />
                          </ListItemIcon>
                          <ListItemText
                            disableTypography
                            primary="All"
                            sx={{
                              fontSize: "14px",
                              fontWeight:
                                paymentListchecked.length ===
                                PAYMENT_ARRAY.length
                                  ? "600 !important"
                                  : "500 !important",
                            }}
                          />
                        </ListItemButton>
                      </List>
                      {PAYMENT_ARRAY
                        .filter((payment) => payment !== "")
                        .map((payment) => (
                          <List
                            component="div"
                            key={payment}
                            sx={{
                              paddingTop: "2px!important",
                              paddingBottom: "2px!important",
                            }}
                          >
                            <ListItemButton
                              sx={{
                                minWidth: "0px !important",
                                borderRadius: "4px!important",
                                paddingTop: "0px!important",
                                paddingBottom: "0px!important",
                                height: "30px!important",
                                backgroundColor: paymentListchecked.includes(
                                  payment
                                )
                                  ? "#E5F3FF!important"
                                  : "",
                                color: paymentListchecked.includes(payment)
                                  ? "#0476E2!important"
                                  : "#2A2C2D!important",

                                cursor: "default",
                              }}
                              onClick={handlePaymentToggle(payment)}
                            >
                              <ListItemIcon sx={{ minWidth: "20px" }}>
                                <Checkbox
                                  edge="start"
                                  checked={paymentListchecked.includes(payment)}
                                  tabIndex={-1}
                                  disableRipple
                                  slotProps={{ "aria-labelledby": payment }}
                                  sx={{ padding: 0, marginRight: 1 }}
                                />
                              </ListItemIcon>
                              <ListItemText
                                disableTypography
                                sx={{
                                  fontSize: "14px",
                                  fontWeight: paymentListchecked.includes(
                                    payment
                                  )
                                    ? "600!important"
                                    : "500!important",
                                }}
                                primary={payment}
                              />
                            </ListItemButton>
                          </List>
                        ))}
                    </div>
                  </Collapse>
                </List>
              </div>
            )}
          </div>
          <div
            className="filterFooterCls"
            style={{
              position: "relative",
              marginTop: "auto",
              flexShrink: 0,
            }}
          >
            <Button
              onClick={handleResetChange}
              color="primary"
              sx={{
                textTransform: "none",
                background: "#1214151A",
                color: "#2A2C2D",
              }}
            >
              Clear All
            </Button>
            <div>
              <Button
                onClick={toggleDrawer(false)}
                color="primary"
                sx={{
                  textTransform: "none",
                  background: "#1214151A",
                  color: "#2A2C2D",
                }}
              >
                Cancel
              </Button>
              &nbsp;&nbsp;
              <Button
                color="primary"
                variant="contained"
                sx={{ textTransform: "none" }}
                onClick={handleApplyFilters}
                disabled={
                  statusListchecked.length === 0 ||
                  categoryListChecked.length === 0 ||
                  countryListchecked.length === 0 ||
                  apiLevelListchecked.length === 0 ||
                  paymentListchecked.length === 0
                }
              >
                Apply
              </Button>
            </div>
          </div>
        </div>
      </Drawer>
      <PopupDialog
        openErrorPopup={openErrorPopup}
        errorMessage={errorMessage}
        headerMessage={headerMessage}
        componentName={componentName}
      />
    </>
  );
};

GlobalAdminDrawerFilter.propTypes = {
  openFilter: PropTypes.bool.isRequired,
  toggleDrawer: PropTypes.func.isRequired,
  categoryObjectList: PropTypes.object.isRequired,
  setCategoryObjectList: PropTypes.func.isRequired,
  modelObjectList: PropTypes.object.isRequired,
  handleFilterApiCalls: PropTypes.func.isRequired,
  channelListchecked: PropTypes.arrayOf(PropTypes.string).isRequired,
  setChannelListchecked: PropTypes.func.isRequired,
  setChannelSelections: PropTypes.func.isRequired,
  categorySelection: PropTypes.object.isRequired,
  setCategorySelection: PropTypes.func.isRequired,
  setModelSelections: PropTypes.func.isRequired,
  paymentSelections: PropTypes.object.isRequired,
  setPaymentSelections: PropTypes.func.isRequired,
  countrySelections: PropTypes.object.isRequired,
  setCountrySelections: PropTypes.func.isRequired,
  statusSelections: PropTypes.object.isRequired,
  setStatusSelections: PropTypes.func.isRequired,
  apiLevelSelections: PropTypes.object.isRequired,
  setApiLevelSelections: PropTypes.func.isRequired,
  setEcuTypeSelections: PropTypes.func.isRequired,
  setCpuTypeSelections: PropTypes.func.isRequired,
};
